import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CampaignOutlined,
  Close as CloseIcon,
  EditOutlined,
  Remove as RemoveIcon,
  VisibilityOffOutlined,
} from '@mui/icons-material';
import { api } from '../../helpers/apiUtils';

/**
 * Leseansicht für den Moderator (Plan 2026-11-04): wie er das Webinar einleitet und bewirbt, im Vollbild und auf
 * Abstand lesbar. Termin, Sprecher und Aufforderung setzt der Server beim Abruf, gespeichert ist nur der neutrale
 * Text. Einblenden bleibt ein eigener Klick, der Knopf steht immer sichtbar unten.
 *
 * Dunkler Grund mit festen Farben: im Studio blendet eine helle Fläche, und die Themenfarben sind hier nicht
 * verlässlich (.claude/rules/react-frontend.md § Table styling).
 */

export interface KartenBezug {
  kind: 'anmeldung' | 'wiederholung' | 'aufzeichnung';
  event_id: string;
}

interface Anzeige {
  state: 'ready' | 'key_points_only' | 'pending';
  ai_draft: boolean;
  edited: boolean;
  outdated: boolean;
  failed: boolean;
  generating: boolean;
  voice: 'ich' | 'dritt';
  voice_auto: 'ich' | 'dritt';
  moderator: string | null;
  intro: string;
  closing: string;
  full_text: string[];
  key_points: string[];
  facts: { title: string; host: string | null; when: string | null };
  draft: { full_text: string[]; key_points: string[]; updated_at: string | null };
  warnings: string[];
  text_kind: 'vorab' | 'nachher';
  source_webinar_id: string;
}

const FARBE = {
  grund: '#0C0F13',
  flaeche: '#171B21',
  linie: '#2A313A',
  text: '#F4F6F8',
  leise: '#A9B4C0',
  akzent: '#FFB454',
  gruen: '#7BE0A3',
} as const;

const SCHRIFT_MIN = 22;
const SCHRIFT_MAX = 52;
const SCHRIFT_START = 32;
const SPEICHER_SCHRIFT = 'live-sprechtext-schrift';
const SPEICHER_ANSICHT = 'live-sprechtext-ansicht';
const NACHLADEN_MS = 10_000;

function lies(schluessel: string): string | null {
  try {
    return window.localStorage.getItem(schluessel);
  } catch {
    return null;
  }
}
function merke(schluessel: string, wert: string): void {
  try {
    window.localStorage.setItem(schluessel, wert);
  } catch {
    // Privater Modus: die Einstellung gilt dann nur bis zum Schließen.
  }
}

function fehlerText(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { details?: unknown; error?: string } };
    message?: string;
  };
  const d = e?.response?.data;
  if (typeof d?.details === 'string' && d.details) return d.details;
  return d?.error || e?.message || fallback;
}

const Sprechtext: FC<{
  offen: boolean;
  karte: KartenBezug;
  onClose: () => void;
  /** Einblenden und Ausblenden laufen über die Karte, damit es nur einen Weg gibt. */
  onEinblenden: () => void;
  onAusblenden: () => void;
  sendungLaeuft: boolean;
  /** „noch 4:12", wenn die Karte gerade eingeblendet ist; sonst null. */
  laeuftNoch: string | null;
  busy: boolean;
  knopfText: string;
  /** Fehler vom Einblenden: die Karte liegt hinter der Ansicht, er muss also hier am Knopf stehen. */
  aussenFehler: string | null;
  onAussenFehlerWeg: () => void;
}> = ({
  offen,
  karte,
  onClose,
  onEinblenden,
  onAusblenden,
  sendungLaeuft,
  laeuftNoch,
  busy,
  knopfText,
  aussenFehler,
  onAussenFehlerWeg,
}) => {
  const [daten, setDaten] = useState<Anzeige | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ansicht, setAnsicht] = useState<'volltext' | 'stichpunkte'>(() =>
    lies(SPEICHER_ANSICHT) === 'stichpunkte' ? 'stichpunkte' : 'volltext',
  );
  const [schrift, setSchrift] = useState<number>(() => {
    const n = Number(lies(SPEICHER_SCHRIFT));
    return Number.isFinite(n) && n >= SCHRIFT_MIN && n <= SCHRIFT_MAX ? n : SCHRIFT_START;
  });
  const [stimme, setStimme] = useState<'ich' | 'dritt' | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurfText, setEntwurfText] = useState('');
  const [entwurfPunkte, setEntwurfPunkte] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [bestaetigen, setBestaetigen] = useState(false);
  const bearbeitenRef = useRef(false);
  bearbeitenRef.current = bearbeiten;

  const laden = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: Anzeige }>('/api/live/admin/sprechtext', {
        params: {
          kind: karte.kind,
          event_id: karte.event_id,
          ...(stimme ? { voice: stimme } : {}),
        },
      });
      setDaten(res.data.data);
      setFehler(null);
    } catch (err) {
      setFehler(fehlerText(err, 'Der Text konnte nicht geladen werden.'));
    }
  }, [karte.kind, karte.event_id, stimme]);

  // Beim Öffnen und bei jedem Kartenwechsel leer anfangen: sonst stünde kurz der Text eines anderen Webinars da.
  useEffect(() => {
    if (!offen) return;
    setDaten(null);
    setFehler(null);
    setMeldung(null);
    setBearbeiten(false);
    setBestaetigen(false);
    setStimme(null);
  }, [offen, karte.kind, karte.event_id]);

  useEffect(() => {
    if (!offen) return undefined;
    laden();
    const id = setInterval(() => {
      // Beim Bearbeiten nicht nachladen: die Eingabe bliebe zwar, aber der Anker wäre nicht mehr der gelesene.
      if (document.visibilityState === 'visible' && !bearbeitenRef.current) laden();
    }, NACHLADEN_MS);
    return () => clearInterval(id);
  }, [offen, laden]);

  // Das Tablet soll während des Vorlesens nicht in den Ruhezustand gehen. Geht das nicht, ändert sich nichts.
  useEffect(() => {
    if (!offen) return undefined;
    let sperre: { release?: () => Promise<void> } | null = null;
    let beendet = false;
    const nav = navigator as unknown as {
      wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    nav.wakeLock
      ?.request('screen')
      .then((s) => {
        if (beendet) s.release().catch(() => undefined);
        else sperre = s;
      })
      .catch(() => undefined);
    return () => {
      beendet = true;
      sperre?.release?.().catch(() => undefined);
    };
  }, [offen]);

  const aendereSchrift = (d: number) => {
    const n = Math.min(SCHRIFT_MAX, Math.max(SCHRIFT_MIN, schrift + d));
    setSchrift(n);
    merke(SPEICHER_SCHRIFT, String(n));
  };
  const aendereAnsicht = (a: 'volltext' | 'stichpunkte') => {
    setAnsicht(a);
    merke(SPEICHER_ANSICHT, a);
  };

  const oeffneBearbeiten = () => {
    if (!daten) return;
    setEntwurfText(daten.draft.full_text.join('\n\n'));
    setEntwurfPunkte(daten.draft.key_points.join('\n'));
    setMeldung(null);
    setBearbeiten(true);
  };

  const speichern = async () => {
    if (!daten) return;
    setSpeichert(true);
    setFehler(null);
    try {
      const res = await api.patch<{ success: boolean; data: { warnings: string[] } }>(
        `/api/live/admin/sprechtext/${encodeURIComponent(daten.source_webinar_id)}/${daten.text_kind}`,
        {
          full_text: entwurfText
            .split(/\n\s*\n/)
            .map((s) => s.trim())
            .filter(Boolean),
          key_points: entwurfPunkte
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          updated_at: daten.draft.updated_at,
        },
      );
      const w = res.data.data.warnings;
      setMeldung(w.length ? `Gespeichert, aber bitte prüfen: ${w.join(' · ')}` : 'Gespeichert.');
      setBearbeiten(false);
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Speichern hat nicht geklappt.'));
    } finally {
      setSpeichert(false);
    }
  };

  const neuErzeugen = async (bestaetigt: boolean) => {
    if (!daten) return;
    setFehler(null);
    setBestaetigen(false);
    try {
      await api.post(
        `/api/live/admin/sprechtext/${encodeURIComponent(daten.source_webinar_id)}/${daten.text_kind}/regenerate`,
        {
          confirm_overwrite: bestaetigt,
        },
      );
      setMeldung('Der Text wird neu erzeugt. Das dauert meist ein bis zwei Minuten.');
      await laden();
    } catch (err) {
      const d = (err as { response?: { data?: { requires_confirmation?: boolean } } })?.response
        ?.data;
      if (d?.requires_confirmation) {
        setMeldung(null);
        setBestaetigen(true);
      } else {
        setFehler(fehlerText(err, 'Das hat nicht geklappt.'));
      }
    }
  };

  const koerperStil = {
    fontSize: schrift,
    lineHeight: 1.5,
    color: FARBE.text,
    fontWeight: 500,
  } as const;
  const zeigeVolltext = ansicht === 'volltext' && !!daten && daten.full_text.length > 0;

  return (
    <Dialog
      open={offen}
      fullScreen
      onClose={onClose}
      // Escape würde beim Bearbeiten die Eingabe verwerfen.
      disableEscapeKeyDown={bearbeiten}
      PaperProps={{
        sx: { bgcolor: FARBE.grund, color: FARBE.text, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Kopf: bleibt stehen, der Text scrollt darunter. */}
      <Box
        sx={{
          flex: '0 0 auto',
          borderBottom: `1px solid ${FARBE.linie}`,
          bgcolor: FARBE.flaeche,
          px: 2,
          py: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={ansicht}
            onChange={(_, w) => w && aendereAnsicht(w)}
            aria-label="Ansicht"
            sx={{
              '& .MuiToggleButton-root': { color: FARBE.leise, borderColor: FARBE.linie, px: 2 },
              '& .Mui-selected': {
                color: `${FARBE.grund} !important`,
                bgcolor: `${FARBE.text} !important`,
              },
            }}
          >
            <ToggleButton value="volltext">Volltext</ToggleButton>
            <ToggleButton value="stichpunkte">Stichpunkte</ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Schrift kleiner">
              <span>
                <IconButton
                  aria-label="Schrift kleiner"
                  size="large"
                  onClick={() => aendereSchrift(-4)}
                  disabled={schrift <= SCHRIFT_MIN}
                  sx={{ color: FARBE.text }}
                >
                  <RemoveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Schrift größer">
              <span>
                <IconButton
                  aria-label="Schrift größer"
                  size="large"
                  onClick={() => aendereSchrift(4)}
                  disabled={schrift >= SCHRIFT_MAX}
                  sx={{ color: FARBE.text }}
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {daten && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={daten.voice}
              onChange={(_, w) => w && setStimme(w)}
              aria-label="Sprecher"
              sx={{
                '& .MuiToggleButton-root': {
                  color: FARBE.leise,
                  borderColor: FARBE.linie,
                  textTransform: 'none',
                },
                '& .Mui-selected': {
                  color: `${FARBE.grund} !important`,
                  bgcolor: `${FARBE.text} !important`,
                },
              }}
            >
              <ToggleButton value="ich">Mein Webinar</ToggleButton>
              <ToggleButton value="dritt">
                {daten.facts.host ? `Mit ${daten.facts.host}` : 'Anderes Webinar'}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Box sx={{ flex: 1 }} />
          {daten?.ai_draft && (
            <Chip
              size="small"
              label="KI-Entwurf"
              sx={{ bgcolor: FARBE.akzent, color: FARBE.grund, fontWeight: 700 }}
            />
          )}
          {daten?.edited && (
            <Chip
              size="small"
              label="Von Hand bearbeitet"
              sx={{ bgcolor: FARBE.gruen, color: FARBE.grund, fontWeight: 700 }}
            />
          )}
          {daten && !bearbeiten && (
            <Button
              startIcon={<EditOutlined />}
              onClick={oeffneBearbeiten}
              sx={{ color: FARBE.text }}
            >
              Bearbeiten
            </Button>
          )}
          <IconButton
            aria-label="Schließen"
            size="large"
            onClick={onClose}
            disabled={bearbeiten}
            sx={{ color: FARBE.text, '&.Mui-disabled': { color: FARBE.linie } }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Text: scrollt von Hand. */}
      <Box sx={{ flex: '1 1 auto', overflowY: 'auto', px: { xs: 2.5, md: 6 }, py: 3 }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          {fehler && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fehler}
            </Alert>
          )}
          {bestaetigen && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <>
                  <Button color="inherit" size="small" onClick={() => neuErzeugen(true)}>
                    Trotzdem neu erzeugen
                  </Button>
                  <Button color="inherit" size="small" onClick={() => setBestaetigen(false)}>
                    Abbrechen
                  </Button>
                </>
              }
            >
              Der Text wurde von Hand bearbeitet. Eine Neuerzeugung ersetzt diese Fassung.
            </Alert>
          )}
          {meldung && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMeldung(null)}>
              {meldung}
            </Alert>
          )}
          {!daten && !fehler && (
            <Box display="flex" justifyContent="center" my={6}>
              <CircularProgress sx={{ color: FARBE.text }} />
            </Box>
          )}

          {daten && bearbeiten && (
            <Stack spacing={2}>
              <Typography sx={{ color: FARBE.leise }}>
                Einstieg, Termin, Name und der Satz zum Link kommen automatisch dazu. Hier steht nur
                der Mittelteil.
              </Typography>
              {[
                {
                  titel: 'Mittelteil zum Vorlesen (Leerzeile = neuer Absatz)',
                  wert: entwurfText,
                  setzen: setEntwurfText,
                  zeilen: 9,
                },
                {
                  titel: 'Stichpunkte (je Zeile ein Punkt)',
                  wert: entwurfPunkte,
                  setzen: setEntwurfPunkte,
                  zeilen: 5,
                },
              ].map((f) => (
                <TextField
                  key={f.titel}
                  label={f.titel}
                  multiline
                  minRows={f.zeilen}
                  fullWidth
                  value={f.wert}
                  onChange={(e) => f.setzen(e.target.value)}
                  InputLabelProps={{ shrink: true, sx: { color: FARBE.leise } }}
                  InputProps={{ sx: { color: FARBE.text, fontSize: 20, bgcolor: FARBE.flaeche } }}
                />
              ))}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={speichern}
                  disabled={speichert}
                  sx={{
                    bgcolor: FARBE.gruen,
                    color: FARBE.grund,
                    fontWeight: 700,
                    '&:hover': { bgcolor: FARBE.gruen },
                  }}
                >
                  {speichert ? <CircularProgress size={18} color="inherit" /> : 'Speichern'}
                </Button>
                <Button
                  onClick={() => setBearbeiten(false)}
                  disabled={speichert}
                  sx={{ color: FARBE.text }}
                >
                  Abbrechen
                </Button>
              </Stack>
            </Stack>
          )}

          {daten && !bearbeiten && (
            <>
              <Typography sx={{ color: FARBE.leise, fontSize: 16, mb: 2 }}>
                {[daten.facts.when, daten.facts.title, daten.facts.host]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {daten.outdated && !daten.edited && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Die Seite wurde geändert. Ein neuer Text kommt in den nächsten Minuten, bis dahin
                  nur Stichpunkte.
                </Alert>
              )}
              {daten.outdated && daten.edited && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => neuErzeugen(false)}>
                      Neu erzeugen
                    </Button>
                  }
                >
                  Die Seite wurde geändert, seit dieser Text bearbeitet wurde. Bitte die Zahlen
                  prüfen.
                </Alert>
              )}
              {daten.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Bitte prüfen: {daten.warnings.join(' · ')}
                </Alert>
              )}
              {ansicht === 'volltext' && !zeigeVolltext && (
                <Alert
                  severity={daten.failed ? 'warning' : 'info'}
                  sx={{ mb: 2 }}
                  action={
                    daten.generating ? undefined : (
                      <Button color="inherit" size="small" onClick={() => neuErzeugen(false)}>
                        Jetzt erzeugen
                      </Button>
                    )
                  }
                >
                  {daten.generating
                    ? 'Der Text wird gerade erzeugt. Das dauert meist ein bis zwei Minuten.'
                    : daten.state === 'pending'
                      ? 'Für dieses Webinar gibt es noch keinen Text. Er entsteht in den nächsten Minuten automatisch.'
                      : daten.failed
                        ? 'Der Text konnte gerade nicht erzeugt werden. Unten stehen die Punkte, die sicher belegt sind.'
                        : 'Es gibt keinen geprüften Vorlesetext. Unten stehen die Punkte, die sicher belegt sind.'}
                </Alert>
              )}

              {zeigeVolltext ? (
                <Stack spacing={3}>
                  {daten.full_text.map((absatz) => (
                    <Typography key={absatz} component="p" sx={koerperStil}>
                      {absatz}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Stack spacing={3}>
                  <Typography component="p" sx={koerperStil}>
                    {daten.intro}
                  </Typography>
                  {daten.key_points.length > 0 && (
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {daten.key_points.map((p) => (
                        <Typography key={p} component="li" sx={koerperStil}>
                          {p}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <Typography component="p" sx={koerperStil}>
                    {daten.closing}
                  </Typography>
                </Stack>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Fuß: bleibt stehen, damit Carsten nicht aus dem Text heraus muss. */}
      <Box
        sx={{
          flex: '0 0 auto',
          borderTop: `1px solid ${FARBE.linie}`,
          bgcolor: FARBE.flaeche,
          px: 2,
          py: 1.5,
        }}
      >
        {aussenFehler && (
          <Alert
            severity="error"
            sx={{ maxWidth: 1000, mx: 'auto', mb: 1 }}
            onClose={onAussenFehlerWeg}
          >
            {aussenFehler}
          </Alert>
        )}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ maxWidth: 1000, mx: 'auto' }}>
          {laeuftNoch ? (
            <>
              <Typography sx={{ color: FARBE.gruen, fontWeight: 700, fontSize: 18, flex: 1 }}>
                Läuft · {laeuftNoch}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<VisibilityOffOutlined />}
                onClick={onAusblenden}
                disabled={busy}
                sx={{ fontWeight: 700, minHeight: 56, px: 4 }}
              >
                Ausblenden
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ color: FARBE.leise, fontSize: 15, flex: 1 }}>
                {sendungLaeuft
                  ? 'Zuschauer sehen das Angebot unter dem Video, sobald der Klick in ihrem Bild ankommt.'
                  : 'Einblenden geht erst während der laufenden Sendung.'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<CampaignOutlined />}
                onClick={onEinblenden}
                disabled={!sendungLaeuft || busy}
                sx={{
                  fontWeight: 700,
                  minHeight: 56,
                  px: 4,
                  bgcolor: FARBE.akzent,
                  color: FARBE.grund,
                  '&:hover': { bgcolor: FARBE.akzent },
                  '&.Mui-disabled': { bgcolor: FARBE.linie, color: FARBE.leise },
                }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : knopfText}
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default Sprechtext;
